/* eslint-disable react-hooks/exhaustive-deps */
import Button from "components/Button";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useAppScroll, useIsMobile } from "hooks";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useWindowSize } from "react-use";
import urls from "utils/urls";
import styles from "./styles.module.scss";

interface Props {
  onOpenQuotationModal: () => void;
}

const Home: React.FC<Props> = ({ onOpenQuotationModal }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentIndex = useRef(0);

  const windowSize = useWindowSize();
  const isMobile = useIsMobile();
  const { scrollTo } = useAppScroll();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const totalImages = 112;

  const images = useMemo(() => {
    return Array(totalImages)
      .fill(0)
      .map((_, index) => {
        const img = new Image();
        img.src = urls.images(`home/${index + 1}.webp`);
        if (index === 0) {
          img.onload = () => showImageWithIndex(0);
        }
        return img;
      });
  }, []);

  useEffect(() => {
    showImageWithIndex(currentIndex.current);
  }, [windowSize]);

  const showImageWithIndex = useCallback(
    (index: number) => {
      const image = images[index];

      if (canvasRef.current && image && image.complete) {
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        if (context) {
          const dpr = window.devicePixelRatio || 1;
          const canvasWidth = canvas.clientWidth * dpr;
          const canvasHeight = canvas.clientHeight * dpr;

          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          const scaleX = canvasWidth / image.width;
          const scaleY = canvasHeight / image.height;

          const scale = Math.max(scaleX, scaleY);

          // Center the image on the canvas
          const offsetX = (canvasWidth - image.width * scale) / 2;
          const offsetY = (canvasHeight - image.height * scale) / 2;

          // Clear previous frame
          context.clearRect(0, 0, canvasWidth, canvasHeight);

          // Draw the image with high quality
          context.drawImage(
            image,
            offsetX,
            offsetY,
            image.width * scale,
            image.height * scale
          );
        }
      }
    },
    [images]
  );

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    currentIndex.current = Math.round(latest * totalImages);
    showImageWithIndex(currentIndex.current);
  });

  return (
    <div className={styles.container} id="home" ref={containerRef}>
      <div className={styles.content}>
        <canvas className={styles.canvas} ref={canvasRef} />

        <div className={styles.details}>
          <motion.h1
            className={styles.title}
            initial={{ opacity: 0, y: 15 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <span className={styles.highlight}>Renewable Energy,</span>
            {" Made for Every Filipino"}
          </motion.h1>

          <motion.p
            className={styles.subtitle}
            initial={{ opacity: 0, y: 15 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            Harnessing expertise, affordability, and innovation to bring
            sustainable energy solutions
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            transition={{ duration: 0.5, delay: 1.1 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            {isMobile ? (
              <Button
                className={styles.button}
                type="primary"
                onClick={onOpenQuotationModal}
              >
                Get a Free Quote
              </Button>
            ) : (
              <Button
                className={styles.button}
                type="primary"
                onClick={() => scrollTo({ elementId: "about", offset: 20 })}
                showArrowIcon
              >
                Learn More
              </Button>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Home;
